import React, { useState, useEffect, useRef } from 'react';

export default function KanbanOverlayWidget({ compact = false, flannelStyle = false, onTaskDone, active = true }) {
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const previousDoneItemsRef = useRef(new Set());
  const lastAnimationTimeRef = useRef(0);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await fetch('/api/admin?action=kanban');
        const data = await response.json();
        
        if (data.success) {
          const allItems = data.items || [];
          
          // Filter to only "In Progress" items for display
          const inProgressItems = allItems
            .filter(item => item.column === 'In Progress')
            .sort((a, b) => (b.order || 0) - (a.order || 0)); // Most recent first
          
          setItems(inProgressItems);

          // Check for newly completed tasks
          if (onTaskDone) {
            const doneItems = allItems.filter(item => item.column === 'Done');
            const currentDoneIds = new Set(doneItems.map(item => item.id));
            const previousDoneIds = previousDoneItemsRef.current;

            // On first load, just initialize the set without triggering animations
            if (!isInitializedRef.current) {
              previousDoneItemsRef.current = currentDoneIds;
              isInitializedRef.current = true;
            } else {
              // Find new items in Done
              for (const doneItem of doneItems) {
                if (!previousDoneIds.has(doneItem.id)) {
                  // Item is newly in Done column - check if it's recent enough
                  let shouldTrigger = true;
                  
                  if (doneItem.finishedAt) {
                    // Check if finishedAt is recent (within last 30 seconds to account for polling delay)
                    try {
                      const finishedTime = new Date(doneItem.finishedAt).getTime();
                      const now = Date.now();
                      const timeDiff = (now - finishedTime) / 1000; // seconds

                      // Only trigger if timestamp is within last 30 seconds (very lenient)
                      // This prevents triggering on very old items that might have been moved around
                      if (timeDiff > 30) {
                        shouldTrigger = false;
                        console.log('Skipping old task:', doneItem.title, 'Time diff:', timeDiff);
                      } else {
                        console.log('Task completed detected:', doneItem.title, 'Time diff:', timeDiff);
                      }
                    } catch (e) {
                      console.error('Error parsing finishedAt:', e, doneItem);
                      // If we can't parse the timestamp, still trigger if it's a new item
                      shouldTrigger = true;
                    }
                  } else {
                    // No finishedAt timestamp, but item is newly in Done - trigger anyway
                    console.log('Task completed detected (no timestamp):', doneItem.title);
                  }

                  if (shouldTrigger) {
                    // Throttle: only trigger animation if last one was more than 3 seconds ago
                    const now = Date.now();
                    const timeSinceLastAnimation = now - lastAnimationTimeRef.current;
                    if (timeSinceLastAnimation >= 3000) {
                      lastAnimationTimeRef.current = now;
                      console.log('Triggering animation for:', doneItem.title);
                      onTaskDone(doneItem);
                    } else {
                      console.log('Animation throttled, time since last:', timeSinceLastAnimation);
                    }
                  }
                }
              }

              // Update previous Done items
              previousDoneItemsRef.current = currentDoneIds;
            }
          }
        }
      } catch (err) {
        console.error('Error fetching kanban items:', err);
      } finally {
        setLoading(false);
      }
    };

    // Always fetch once so the widget shows real state, even when offline
    fetchItems();

    // Stop polling entirely when the stream is offline (`active` is false) so an
    // idle OBS overlay makes no requests.
    if (!active) return undefined;

    // Refresh every 15 seconds — fast enough for OBS updates while staying
    // within Vercel's edge request limits (2s polling was ~43k requests/day)
    const interval = setInterval(fetchItems, 15000);
    return () => clearInterval(interval);
  }, [onTaskDone, active]);

  // Rotate through items every 10 seconds
  useEffect(() => {
    if (items.length <= 1) return;

    const rotationInterval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % items.length);
    }, 10000); // Rotate every 10 seconds

    return () => clearInterval(rotationInterval);
  }, [items.length]);

  // Compact flannel mode
  if (compact && flannelStyle) {
    const currentItem = items[currentIndex];
    const displayText = loading ? 'Loading...' : (items.length === 0 ? 'Chillin!' : (currentItem?.title || 'Untitled'));

    return (
      <div className="kanban-flannel">
        <span className="kanban-flannel-label">WORKING ON</span>
        <div className="kanban-flannel-content">
          <span className="kanban-flannel-value">{displayText}</span>
        </div>
        <style>{`
          .kanban-flannel {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 12px;
            font-family: 'Press Start 2P', monospace;
            width: 100%;
          }
          .kanban-flannel-label {
            font-size: 0.85rem;
            color: #d4c4a8;
            text-shadow: 1px 1px 0 #0d4a54;
            letter-spacing: 0.05em;
            line-height: 1.4;
          }
          .kanban-flannel-content {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .kanban-flannel-value {
            font-size: 0.8rem;
            font-weight: 700;
            color: #f5f0e6;
            text-shadow: 1px 1px 0 #a65241;
            line-height: 1.5;
            word-break: break-word;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-align: center;
          }
        `}</style>
      </div>
    );
  }

  // Regular compact mode
  if (compact) {
    if (loading) {
      return (
        <div className="kanban-compact">
          <span className="kanban-compact-label">WORKING ON</span>
          <span className="kanban-compact-value">Loading...</span>
          <style>{`
            .kanban-compact {
              display: flex;
              align-items: center;
              gap: 8px;
              font-family: 'JetBrains Mono', monospace;
            }
            .kanban-compact-label {
              font-size: 0.625rem;
              color: #dc2626;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              font-weight: 600;
              white-space: nowrap;
            }
            .kanban-compact-value {
              font-size: 0.8125rem;
              font-weight: 600;
              color: #d4d4d4;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 180px;
            }
          `}</style>
        </div>
      );
    }

    const currentItem = items[currentIndex];
    const displayText = items.length === 0 ? 'Nothing right now' : (currentItem?.title || 'Untitled');

    return (
      <div className="kanban-compact">
        <span className="kanban-compact-label">WORKING ON</span>
        <span className="kanban-compact-value">{displayText}</span>
        {items.length > 1 && (
          <span className="kanban-compact-count">+{items.length - 1}</span>
        )}
        <style>{`
          .kanban-compact {
            display: flex;
            align-items: center;
            gap: 8px;
            font-family: 'JetBrains Mono', monospace;
          }
          .kanban-compact-label {
            font-size: 0.625rem;
            color: #dc2626;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-weight: 600;
            white-space: nowrap;
          }
          .kanban-compact-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #d4d4d4;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 180px;
          }
          .kanban-compact-count {
            font-size: 0.625rem;
            color: #94a3b8;
            background: rgba(220, 38, 38, 0.2);
            padding: 2px 6px;
            border-radius: 4px;
          }
        `}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="kanban-overlay-widget">
        <div className="kanban-loading">Loading...</div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="kanban-overlay-widget">
        <div className="kanban-empty">No items in progress</div>
      </div>
    );
  }

  const currentItem = items[currentIndex];

  return (
    <div className="kanban-overlay-widget">
      <div className="kanban-header">
        <span className="kanban-label">WORKING ON</span>
      </div>
      <div className="kanban-card">
        <div className="kanban-title">{currentItem.title || 'Untitled'}</div>
        {currentItem.description && (
          <div className="kanban-description">{currentItem.description}</div>
        )}
      </div>
      {items.length > 1 && (
        <div className="kanban-indicator">
          {items.map((_, index) => (
            <span
              key={index}
              className={`kanban-dot ${index === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
      <style>{`
        .kanban-overlay-widget {
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(244, 114, 182, 0.3);
          border-radius: 6px;
          padding: 0.5rem;
          min-width: 200px;
          max-width: 280px;
          font-family: 'JetBrains Mono', monospace;
        }

        .kanban-header {
          margin-bottom: 0.375rem;
        }

        .kanban-label {
          font-size: 0.625rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-weight: 600;
        }

        .kanban-card {
          background: rgba(26, 26, 46, 0.8);
          border: 1px solid rgba(244, 114, 182, 0.2);
          border-radius: 4px;
          padding: 0.5rem;
        }

        .kanban-title {
          font-size: 0.875rem;
          font-weight: 700;
          color: #f472b6;
          margin-bottom: 0.25rem;
          line-height: 1.3;
        }

        .kanban-description {
          font-size: 0.75rem;
          color: #e2e8f0;
          line-height: 1.4;
          opacity: 0.9;
          white-space: pre-wrap;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .kanban-loading,
        .kanban-empty {
          font-size: 0.75rem;
          color: #94a3b8;
          text-align: center;
          padding: 0.5rem;
        }

        .kanban-indicator {
          display: flex;
          gap: 0.25rem;
          justify-content: center;
          margin-top: 0.375rem;
        }

        .kanban-dot {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: rgba(244, 114, 182, 0.3);
          transition: all 0.3s ease;
        }

        .kanban-dot.active {
          background: #f472b6;
          width: 8px;
          box-shadow: 0 0 8px rgba(244, 114, 182, 0.6);
        }
      `}</style>
    </div>
  );
}

